import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Pagination, Tooltip } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { MdSettings, MdInventory2, MdPowerSettingsNew, MdPause } from 'react-icons/md'
import { TbAdjustments } from 'react-icons/tb'
import ApiStateView from '../ApiState'
import AddressModal from '../AddressModal'
import LogiSearch from '../LogiSearch'
import SectionShell from '../SectionShell'
import SectionTitle from '../SectionTitle'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useUserId } from '../../hooks/useUserId'
import { rulesApi, labelsApi, StoreRuleDto, StoreAddressInput, UpsertRuleInput } from '../../api'
import { COURIER } from '../../constants'

type Field = 'inm' | 'sem' | 'dias'
type EditMap = Record<number, Partial<Record<Field, string>>>
type SafetyCodigo = 'caja' | 'bolsa_a' | 'bolsa_b' | 'sello'
const SAFETY_CODIGOS: SafetyCodigo[] = ['caja', 'bolsa_a', 'bolsa_b', 'sello']
const SAFETY_LABEL: Record<SafetyCodigo, string> = { caja: 'Caja', bolsa_a: 'Bolsa A', bolsa_b: 'Bolsa B', sello: 'Sello' }

const ROWS_PER_PAGE = 10
const PAGE_SIZES = [10, 25, 50, 100]

// Día de recolección semanal · ISO 1=Lun … 7=Dom (null = sin día asignado).
// Lunes (1) queda bloqueado: la evaluación corre el día anterior y generar la
// guía el domingo (fin de semana) no permite resolver fallos. Mín. permitido: Martes.
const DIAS_SEMANA: Array<{ value: number, label: string }> = [
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' }
]

const ReglasView: FC = () => {
  const idSeller = useSelectedSeller()
  const userId = useUserId()
  const [search, setSearch] = useState('')
  const [edits, setEdits] = useState<EditMap>({})
  const [internoEdits, setInternoEdits] = useState<Record<number, boolean>>({})
  // Encendido/apagado de la tienda · edición pendiente hasta "Guardar" (no auto).
  const [activoEdits, setActivoEdits] = useState<Record<number, boolean>>({})
  const [diaEdits, setDiaEdits] = useState<Record<number, number | null>>({})
  // Cadencia "cada N semanas" por tienda (strings en edición).
  const [cadaNEdits, setCadaNEdits] = useState<Record<number, string>>({})
  // Safety por insumo · se edita en un modal (botón por fila).
  const [safetyTarget, setSafetyTarget] = useState<StoreRuleDto | null>(null)
  const [safetyForm, setSafetyForm] = useState<Record<SafetyCodigo, string>>({ caja: '', bolsa_a: '', bolsa_b: '', sello: '' })
  const [savingSafety, setSavingSafety] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)
  const [savingAll, setSavingAll] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE)

  const data = useApi(useCallback(async () => await rulesApi.list(idSeller), [idSeller]), [idSeller])

  const rows = useMemo(
    () => (data.data ?? []).filter(r => r.store.toLowerCase().includes(search.toLowerCase())),
    [data.data, search]
  )

  // Reinicia a la página 1 cuando cambia el filtro/los datos/el tamaño de página.
  useEffect(() => { setPage(1) }, [search, data.data, pageSize])

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const paged = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize]
  )

  const baseOf = (r: StoreRuleDto, f: Field): string => {
    if (f === 'inm') return r.override.min_equipos_inmediata?.toString() ?? ''
    if (f === 'sem') return r.override.min_equipos_semanal?.toString() ?? ''
    return r.override.max_dias_sin_retiro?.toString() ?? ''
  }

  const valOf = (r: StoreRuleDto, f: Field): string => edits[r.id_store]?.[f] ?? baseOf(r, f)
  const setVal = (idStore: number, f: Field, v: string): void => {
    setEdits(prev => ({ ...prev, [idStore]: { ...prev[idStore], [f]: v } }))
  }

  // Tipo de envío · null = no configurado (el admin debe elegir DHL o Reuse).
  const internoOf = (r: StoreRuleDto): boolean | null =>
    r.id_store in internoEdits ? internoEdits[r.id_store] : r.recoleccion_interna
  const setInterno = (idStore: number, v: boolean): void => {
    setInternoEdits(prev => ({ ...prev, [idStore]: v }))
  }

  const activoOf = (r: StoreRuleDto): boolean => activoEdits[r.id_store] ?? r.activo
  const setActivo = (idStore: number, v: boolean): void => {
    setActivoEdits(prev => ({ ...prev, [idStore]: v }))
  }

  // Día de recolección · SIN default: si la tienda no tiene día, queda "sin día"
  // (la regla semanal no dispara hasta configurarlo).
  const diaOf = (r: StoreRuleDto): number | null =>
    r.id_store in diaEdits ? diaEdits[r.id_store] : r.dia_recoleccion_semanal
  const setDia = (idStore: number, v: number | null): void => {
    setDiaEdits(prev => ({ ...prev, [idStore]: v }))
  }

  const cadaNOf = (r: StoreRuleDto): string =>
    cadaNEdits[r.id_store] ?? String(r.cada_n_semanas ?? 1)
  const setCadaN = (idStore: number, v: string): void => {
    setCadaNEdits(prev => ({ ...prev, [idStore]: v }))
  }

  const tieneCambios = (r: StoreRuleDto): boolean =>
    edits[r.id_store] !== undefined || internoEdits[r.id_store] !== undefined ||
    activoEdits[r.id_store] !== undefined ||
    r.id_store in diaEdits || r.id_store in cadaNEdits

  const toNum = (s: string): number | null => {
    const t = s.trim()
    if (t === '') return null
    const n = Number(t)
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null
  }

  // Validación regla 1 > regla 2 · SOLO si ambos están configurados (sin defaults).
  const reglaError = (r: StoreRuleDto): string | null => {
    const inm = toNum(valOf(r, 'inm'))
    const sem = toNum(valOf(r, 'sem'))
    if (inm !== null && sem !== null && inm <= sem) return `El mínimo inmediato (${inm}) debe ser mayor al semanal (${sem})`
    return null
  }

  // Error de configuración de la fila: regla1>regla2 + tipo de envío obligatorio
  // cuando la tienda va a operar (tiene día o algún mínimo configurado).
  const configError = (r: StoreRuleDto): string | null => {
    const re = reglaError(r)
    if (re !== null) return re
    const operativa = diaOf(r) !== null || toNum(valOf(r, 'inm')) !== null ||
      toNum(valOf(r, 'sem')) !== null || toNum(valOf(r, 'dias')) !== null
    if (operativa && internoOf(r) === null) return 'Selecciona el tipo de envío (DHL o Reuse)'
    return null
  }

  const guardar = useCallback(async (r: StoreRuleDto) => {
    const err = configError(r)
    if (err !== null) { enqueueSnackbar(err, { variant: 'error' }); return }
    setBusy(r.id_store)
    try {
      await rulesApi.upsert({
        id_store: r.id_store,
        min_equipos_inmediata: toNum(valOf(r, 'inm')),
        min_equipos_semanal: toNum(valOf(r, 'sem')),
        max_dias_sin_retiro: toNum(valOf(r, 'dias')),
        recoleccion_interna: internoOf(r),
        distancia: r.distancia,
        notas: r.notas,
        dia_recoleccion_semanal: diaOf(r),
        cada_n_semanas: toNum(cadaNOf(r)) ?? 1,
        activo: activoOf(r)
        // safety se edita aparte, desde su modal (no se toca aquí)
      }, idSeller, userId)
      enqueueSnackbar(`Reglas de ${r.store} guardadas`, { variant: 'success' })
      setEdits(prev => { const c = { ...prev }; delete c[r.id_store]; return c })
      setInternoEdits(prev => { const c = { ...prev }; delete c[r.id_store]; return c })
      setActivoEdits(prev => { const c = { ...prev }; delete c[r.id_store]; return c })
      setDiaEdits(prev => { const c = { ...prev }; delete c[r.id_store]; return c })
      setCadaNEdits(prev => { const c = { ...prev }; delete c[r.id_store]; return c })
      await data.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo guardar', { variant: 'error' })
    } finally { setBusy(null) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits, internoEdits, activoEdits, diaEdits, cadaNEdits, idSeller, userId, data])

  // Payload de guardado de una fila (reglas, sin safety · ese va por su modal).
  const buildPayload = (r: StoreRuleDto): UpsertRuleInput => ({
    id_store: r.id_store,
    min_equipos_inmediata: toNum(valOf(r, 'inm')),
    min_equipos_semanal: toNum(valOf(r, 'sem')),
    max_dias_sin_retiro: toNum(valOf(r, 'dias')),
    recoleccion_interna: internoOf(r),
    distancia: r.distancia,
    notas: r.notas,
    dia_recoleccion_semanal: diaOf(r),
    cada_n_semanas: toNum(cadaNOf(r)) ?? 1,
    activo: activoOf(r)
  })

  // Guardado masivo: manda todas las tiendas con cambios en una sola llamada
  // (el backend agrupa los cambios de día en un solo correo de periodicidad).
  const guardarTodo = useCallback(async () => {
    const conCambios = (data.data ?? []).filter(tieneCambios)
    if (conCambios.length === 0) return
    const invalidas = conCambios.filter((r) => configError(r) !== null)
    if (invalidas.length > 0) {
      enqueueSnackbar(`Corrige la configuración de ${invalidas.length} tienda(s) antes de guardar todo`, { variant: 'error' })
      return
    }
    setSavingAll(true)
    try {
      const res = await rulesApi.upsertMany(conCambios.map(buildPayload), idSeller, userId)
      enqueueSnackbar(
        `${res.guardadas} tienda(s) guardada(s)${res.errores.length > 0 ? ` · ${res.errores.length} con error` : ''}`,
        { variant: res.errores.length > 0 ? 'warning' : 'success' }
      )
      setEdits({}); setInternoEdits({}); setActivoEdits({}); setDiaEdits({}); setCadaNEdits({})
      await data.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo guardar todo', { variant: 'error' })
    } finally { setSavingAll(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, edits, internoEdits, diaEdits, cadaNEdits, idSeller, userId])

  // ── Safety stock por tienda · modal ──
  const abrirSafety = (r: StoreRuleDto): void => {
    setSafetyTarget(r)
    setSafetyForm({
      caja: r.safety.caja !== null ? String(r.safety.caja) : '',
      bolsa_a: r.safety.bolsa_a !== null ? String(r.safety.bolsa_a) : '',
      bolsa_b: r.safety.bolsa_b !== null ? String(r.safety.bolsa_b) : '',
      sello: r.safety.sello !== null ? String(r.safety.sello) : ''
    })
  }

  const guardarSafety = async (): Promise<void> => {
    const r = safetyTarget
    if (r === null) return
    setSavingSafety(true)
    try {
      // Conserva las reglas actuales de la tienda y solo actualiza el safety.
      await rulesApi.upsert({
        id_store: r.id_store,
        min_equipos_inmediata: r.override.min_equipos_inmediata,
        min_equipos_semanal: r.override.min_equipos_semanal,
        max_dias_sin_retiro: r.override.max_dias_sin_retiro,
        recoleccion_interna: r.recoleccion_interna,
        distancia: r.distancia,
        notas: r.notas,
        dia_recoleccion_semanal: r.dia_recoleccion_semanal ?? undefined,
        safety: {
          caja: toNum(safetyForm.caja),
          bolsa_a: toNum(safetyForm.bolsa_a),
          bolsa_b: toNum(safetyForm.bolsa_b),
          sello: toNum(safetyForm.sello)
        }
      }, idSeller, userId)
      enqueueSnackbar(`Safety stock de ${r.store} guardado`, { variant: 'success' })
      setSafetyTarget(null)
      await data.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo guardar el safety stock', { variant: 'error' })
    } finally { setSavingSafety(false) }
  }

  const segBtn = (active: boolean): string =>
    `px-3 py-1 text-[11px] font-semibold transition-colors ${active ? 'bg-neutral-800 text-white' : 'bg-white text-neutral-500 hover:text-neutral-800'}`

  // ── Editar dirección de la tienda (rondana junto al nombre) ──
  const [addrTarget, setAddrTarget] = useState<{ id_store: number, store: string, initial?: Partial<StoreAddressInput> } | null>(null)
  const [loadingAddr, setLoadingAddr] = useState<number | null>(null)

  const abrirDireccion = useCallback(async (r: StoreRuleDto) => {
    setLoadingAddr(r.id_store)
    try {
      const a = await labelsApi.getAddress(r.id_store, idSeller)
      const initial: Partial<StoreAddressInput> | undefined = a !== null
        ? {
            calle: a.calle ?? '',
            numero: a.numero ?? '',
            colonia: a.colonia ?? '',
            ciudad: a.ciudad ?? '',
            estado_region: a.estado_region ?? '',
            cod_region: a.cod_region ?? undefined,
            cod_comuna: a.cod_comuna ?? undefined,
            nom_comuna: a.nom_comuna ?? undefined,
            cod_localidad: a.cod_localidad ?? undefined,
            nom_localidad: a.nom_localidad ?? undefined,
            codigo_postal: a.codigo_postal ?? '',
            referencia: a.referencia ?? '',
            contacto_nombre: a.contacto_nombre ?? '',
            contacto_telefono: a.contacto_telefono ?? '',
            contacto_email: a.contacto_email ?? ''
          }
        : {}
      setAddrTarget({ id_store: r.id_store, store: r.store, initial })
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo cargar la dirección', { variant: 'error' })
    } finally { setLoadingAddr(null) }
  }, [idSeller])

  const guardarDireccion = useCallback(async (input: StoreAddressInput) => {
    try {
      await labelsApi.upsertAddress(input, idSeller, userId, false)
      enqueueSnackbar('Dirección guardada', { variant: 'success' })
      setAddrTarget(null)
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo guardar la dirección', { variant: 'error' })
    }
  }, [idSeller, userId])

  const pendientes = (data.data ?? []).filter(tieneCambios).length

  return (
    <div className='space-y-4'>
      <SectionShell title='Reglas' icon={TbAdjustments} noPadding>
        <div className='flex items-center justify-between flex-wrap gap-3 px-5 pt-5 pb-3'>
          <SectionTitle
            title='Reglas de recolección por tienda'
            description='Configura por tienda: mínimo inmediato (regla 1), mínimo semanal y su día (regla 2), días sin retiro (regla 3) y el tipo de envío. Lo que dejes vacío queda SIN configurar y esa regla no se aplica.'
          />
          <div className='flex items-center gap-2'>
            <LogiSearch className='w-56' placeholder='Buscar tienda…' value={search} onChange={setSearch} />
            <Button
              size='sm' isLoading={savingAll} isDisabled={savingAll || pendientes === 0}
              onPress={() => { guardarTodo() }}
              className='bg-[#44526B] text-white text-xs h-9'
            >
              Guardar todo{pendientes > 0 ? ` (${pendientes})` : ''}
            </Button>
          </div>
        </div>

        <div className='px-5 pb-5'>
          <ApiStateView
            loading={data.loading} error={data.error} onRetry={data.reload}
            empty={!data.loading && rows.length === 0} emptyText='Sin tiendas activas'
          >
            <div className='overflow-x-auto'>
              <table className='w-full border-collapse'>
                <thead>
                  <tr className='bg-neutral-50'>
                    <th className='text-left px-3 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider'>Tienda</th>
                    <th className='text-center px-3 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider'>Inmediata ≥</th>
                    <th className='text-center px-3 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider'>Mín. semanal ≥</th>
                    <th className='text-center px-3 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider'>Día de recolección</th>
                    <th className='text-center px-3 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider'>Cada (sem.)</th>
                    <th className='text-center px-3 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider'>Días sin retiro</th>
                    <th className='text-center px-3 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider'>Safety stock</th>
                    <th className='text-center px-3 py-2.5 text-[11px] font-medium text-neutral-500 uppercase tracking-wider'>Retiro</th>
                    <th className='text-right px-3 py-2.5' />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((r) => {
                    const interno = internoOf(r)
                    return (
                      <tr key={r.id_store} className='hover:bg-neutral-50/60 transition-colors'>
                        <td className='px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 align-middle'>
                          <div className='flex items-center gap-2'>
                            <Tooltip content='Editar dirección de la tienda' size='sm'>
                              <button
                                type='button'
                                aria-label='Editar dirección'
                                disabled={loadingAddr === r.id_store}
                                onClick={() => { void abrirDireccion(r) }}
                                className={`w-6 h-6 rounded-full border border-neutral-200 flex items-center justify-center text-neutral-600 transition-colors ${loadingAddr === r.id_store ? 'opacity-50' : 'hover:bg-neutral-100'}`}
                              >
                                <MdSettings className={`text-sm ${loadingAddr === r.id_store ? 'animate-spin' : ''}`} />
                              </button>
                            </Tooltip>
                            <span>{r.store}</span>
                          </div>
                        </td>
                        <td className='px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 align-middle'>
                          <div className='w-24 mx-auto'>
                            <Input
                              size='sm' type='number' value={valOf(r, 'inm')}
                              placeholder='sin config'
                              onValueChange={(v) => setVal(r.id_store, 'inm', v)}
                              isInvalid={reglaError(r) !== null}
                              errorMessage={reglaError(r) ?? undefined}
                            />
                          </div>
                        </td>
                        <td className='px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 align-middle'>
                          <div className='w-24 mx-auto'>
                            <Input
                              size='sm' type='number' value={valOf(r, 'sem')}
                              placeholder='sin config'
                              onValueChange={(v) => setVal(r.id_store, 'sem', v)}
                              isInvalid={reglaError(r) !== null}
                            />
                          </div>
                        </td>
                        <td className='px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 align-middle'>
                          <div className='w-36 mx-auto'>
                            <select
                              value={diaOf(r) ?? ''}
                              onChange={(e) => setDia(r.id_store, e.target.value === '' ? null : Number(e.target.value))}
                              className='w-full h-8 text-xs rounded-md border border-neutral-200 bg-white px-2 text-neutral-800 focus:outline-none focus:border-neutral-600'
                            >
                              <option value=''>Sin día</option>
                              {DIAS_SEMANA.map(d => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td className='px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 align-middle'>
                          <div className='w-20 mx-auto'>
                            <Input
                              size='sm' type='number' aria-label='Cada cuántas semanas'
                              value={cadaNOf(r)}
                              placeholder='1'
                              onValueChange={(v) => setCadaN(r.id_store, v)}
                            />
                          </div>
                        </td>
                        <td className='px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 align-middle'>
                          <div className='w-24 mx-auto'>
                            <Input
                              size='sm' type='number' value={valOf(r, 'dias')}
                              placeholder='sin config'
                              onValueChange={(v) => setVal(r.id_store, 'dias', v)}
                            />
                          </div>
                        </td>
                        <td className='px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 align-middle text-center'>
                          <Button
                            size='sm' variant='bordered'
                            className='border-neutral-200 text-neutral-700 text-xs h-8'
                            startContent={<MdInventory2 className='text-sm' />}
                            onPress={() => abrirSafety(r)}
                          >
                            Configurar
                          </Button>
                        </td>
                        <td className='px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 align-middle'>
                          <div className='flex items-center justify-center gap-2'>
                            <div className='inline-flex rounded-lg overflow-hidden border border-neutral-200'>
                              <button type='button' className={segBtn(interno === false)} onClick={() => setInterno(r.id_store, false)}>{COURIER}</button>
                              <button type='button' className={`${segBtn(interno === true)} border-l border-neutral-200`} onClick={() => setInterno(r.id_store, true)}>Retiro Reuse</button>
                            </div>
                            <div className='inline-flex rounded-lg overflow-hidden border border-neutral-200'>
                              <button type='button' className={`${segBtn(activoOf(r))} px-2`} title='Activa' aria-label='Activar tienda' onClick={() => setActivo(r.id_store, true)}><MdPowerSettingsNew className='text-sm' /></button>
                              <button type='button' className={`${segBtn(!activoOf(r))} px-2 border-l border-neutral-200`} title='Pausada' aria-label='Pausar tienda' onClick={() => setActivo(r.id_store, false)}><MdPause className='text-sm' /></button>
                            </div>
                          </div>
                        </td>
                        <td className='px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 align-middle text-right'>
                          <Button
                            size='sm' isLoading={busy === r.id_store}
                            isDisabled={!tieneCambios(r) || configError(r) !== null}
                            onPress={() => { void guardar(r) }}
                            className='bg-[#44526B] text-white text-xs h-8'
                          >
                            Guardar
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className='flex flex-col gap-3 mt-3'>
              {totalPages > 1 && (
                <div className='flex justify-center'>
                  <Pagination isCompact showControls size='sm' page={page} total={totalPages} onChange={setPage} />
                </div>
              )}
              <div className='flex items-center justify-between flex-wrap gap-3 text-xs text-neutral-500'>
                <span>Mostrando {paged.length} de {rows.length} tiendas · los campos vacíos quedan sin configurar (esa regla no se aplica).</span>
                {rows.length > PAGE_SIZES[0] && (
                  <span className='flex items-center gap-2'>
                    Filas por página
                    <select
                      aria-label='Filas por página'
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                      className='h-8 rounded-md border border-neutral-200 bg-white px-2 text-xs text-neutral-800 focus:outline-none focus:border-neutral-600'
                    >
                      {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </span>
                )}
              </div>
            </div>
          </ApiStateView>
        </div>
      </SectionShell>

      {/* Modal · editar dirección de la tienda (rondana) */}
      <AddressModal target={addrTarget} onClose={() => setAddrTarget(null)} onSave={guardarDireccion} />

      {/* Modal · safety stock por tienda */}
      <Modal isOpen={safetyTarget !== null} onClose={() => setSafetyTarget(null)} size='lg'>
        <ModalContent>
          <ModalHeader className='flex flex-col gap-0'>
            <span className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>Safety stock por tienda</span>
            <span className='text-base font-bold text-[#151930]'>{safetyTarget?.store ?? ''}</span>
          </ModalHeader>
          <ModalBody className='gap-3'>
            <p className='text-xs text-[#151930]/60'>
              Define el stock mínimo (safety) por insumo para esta tienda. Un campo vacío queda en 0: no se generan recomendaciones ni se envían insumos de ese código a esta tienda.
            </p>
            <div className='grid grid-cols-2 gap-3'>
              {SAFETY_CODIGOS.map((codigo) => (
                <Input
                  key={codigo}
                  size='sm' type='number' label={SAFETY_LABEL[codigo]}
                  value={safetyForm[codigo]}
                  placeholder='0 (sin config)'
                  onValueChange={(v) => setSafetyForm(prev => ({ ...prev, [codigo]: v }))}
                />
              ))}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='bordered' className='border-gray-200 text-[#151930]' onPress={() => setSafetyTarget(null)}>Cancelar</Button>
            <Button
              className='bg-[#44526B] text-white font-semibold'
              isLoading={savingSafety}
              onPress={() => { void guardarSafety() }}
            >
              Guardar safety
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default ReglasView
